import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {connect} from 'react-redux';
import {useNavigate, useParams, useLocation} from 'react-router-dom';
import {
  EmptyState,
  PageLimit,
  SearchInput,
  TableLayout,
  SecondaryButton,
} from '../../../../components';
import Layout from '../Layout';
import * as Actions from '../../../../store/actions';
import Pagination from '../../../../components/Pagination';
import Loader from '../../../../components/Loader';
import moment from 'moment';
import {PageContext} from '../../../../helpers/context';
import {useQuery} from 'react-query';
import {fetchCases} from '../../../../requests/queries/flows';
import {shortenLargeNumbers} from '../../../../helpers/shortenLargeNumbers';
import {getStatusTextAndStyles} from '../../../../helpers/getStatusTextAndStyles';
import {transformObject} from '../utils';
import {filterOutEmptyValues} from '../../../../helpers';
import FilterV2 from 'components/FilterV2';
import PageNumber from 'components/PageNumber';
import {eye} from 'assets/images/images';

const initialFilters = {
  start: '',
  end: '',
  search: '',
};

function Tabs({activeTab, setActiveTab, pendingCount, completedCount}) {
  return (
    <div className="border w-fit   p-1 border-[#E1E4EA] rounded-lg bg-[#F7F7F7] flex items-center gap-2 text-xs lg:text-tiny font-medium">
      <button
        className={`${
          activeTab === 'pending'
            ? 'bg-white text-black'
            : 'bg-transparent text-[#868C98]'
        } cursor-pointer rounded-md px-4 py-[10px] transition hover:bg-white text-black`}
        onClick={() => setActiveTab('pending')}
      >
        Pending cases{' '}
        <span className="text-grey-500 ml-2 font-medium">
          {pendingCount || 0}
        </span>
      </button>
      <button
        className={`${
          activeTab === 'completed'
            ? 'bg-white text-black'
            : 'bg-transparent text-[#868C98]'
        } cursor-pointer rounded-md px-4 py-[10px] transition hover:bg-white text-black`}
        onClick={() => setActiveTab('completed')}
      >
        Resolved cases{' '}
        <span className="text-grey-500 ml-2 font-medium">
          {completedCount || 0}
        </span>
      </button>
    </div>
  );
}

function FlowsCases() {
  const page = localStorage.getItem('casesPageNumber');
  const navigate = useNavigate();
  const {state} = useLocation();
  const [activeTab, setActiveTab] = useState('pending');
  const [openFilter, setOpenFilter] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [currentPage, setCurrentPage] = useState(page || '1');
  const [limit, setLimit] = useState(10);
  const {slug} = useParams();
  const [counts, setCounts] = useState({
    pending: 0,
    completed: 0,
  });

  // Get flowName from state or localStorage
  const flowName =
    state?.flowName || localStorage.getItem('flowName') || 'Unknown Flow';

  const capitalizedWord = word => word.charAt(0).toUpperCase() + word.slice(1);

  const params = useMemo(
    () =>
      transformObject({
        project_id: slug,
        limit,
        page: currentPage,
        status: capitalizedWord(activeTab),
        ...filters,
      }),
    [activeTab, currentPage, limit, slug, filters],
  );

  const {data: casesData, isLoading} = useQuery(['flow-cases', {params}], () =>
    fetchCases({params}),
  );

  const fetchAllCounts = useCallback(
    async (filters = {}) => {
      try {
        const [pendingData, completedData] = await Promise.all([
          fetchCases({
            params: transformObject({
              project_id: slug,
              status: 'Pending',
              limit: 1,
              page: 1,
              ...filters,
            }),
          }),
          fetchCases({
            params: transformObject({
              project_id: slug,
              status: 'Completed',
              limit: 1,
              page: 1,
              ...filters,
            }),
          }),
        ]);

        setCounts({
          pending: pendingData?.count || 0,
          completed: completedData?.count || 0,
        });

        localStorage.setItem('pending', pendingData?.count || 0);
        localStorage.setItem('completed', completedData?.count || 0);
      } catch (error) {
        console.error('Error fetching counts:', error);
      }
    },
    [slug],
  );

  useEffect(() => {
    fetchAllCounts(filters);
  }, [fetchAllCounts, filters]);

  const {setPageName} = useContext(PageContext);
  useEffect(() => {
    setPageName('easydetect');
  }, [setPageName]);

  // Store flowName in localStorage if received from state
  useEffect(() => {
    if (state?.flowName) {
      localStorage.setItem('flowName', state?.flowName);
    }
  }, [state?.flowName]);

  const pageClick = selected => {
    setCurrentPage(selected);
    localStorage.setItem('casesPageNumber', String(selected));
  };

  const cases = useMemo(
    () => (!isLoading && casesData && casesData?.rows) || [],
    [casesData, isLoading],
  );

  const handleFilter = selectedOptions => {
    const filteredOptions = filterOutEmptyValues(selectedOptions);
    if (filteredOptions.dateRange) {
      filteredOptions.start = filteredOptions.dateRange.start;
      filteredOptions.end = filteredOptions.dateRange.end;
      delete filteredOptions.dateRange;
    }
    setCurrentPage(1);
    setFilters(filteredOptions);
    const searchParams = new URLSearchParams();
    if (filteredOptions.start) searchParams.set('start', filteredOptions.start);
    if (filteredOptions.end) searchParams.set('end', filteredOptions.end);
    if (filteredOptions.search)
      searchParams.set('search', filteredOptions.search);
    navigate(`?${searchParams.toString()}`);
  };

  const toggleFilterVisibility = () => {
    setOpenFilter(prev => !prev);
  };

  return (
    <Layout pageTitle="Cases">
      <div className="flex items-center flex-wrap py-3 gap-3 justify-between mx-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <Tabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          pendingCount={counts.pending}
          completedCount={counts.completed}
        />

        <div className="flex items-center gap-2 flex-wrap">
          <SearchInput
            onChange={value => {
              setFilters(prev => ({...prev, page: 1, search: value}));
            }}
            value={filters.search || ''}
          />
          <SecondaryButton
            buttonText="Filter"
            onClick={toggleFilterVisibility}
          />
        </div>
      </div>
      <div className={`filter-slide-container ${openFilter ? 'open' : ''}`}>
        <FilterV2 onFilterChange={handleFilter} />
      </div>

      <section>
        <TableLayout negativeMargins>
          <thead className="font-semibold uppercase">
            <tr className="bg-white80">
              <th className="pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>case id</span>
                </div>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>Transaction Amount</span>
                </div>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>status</span>
                </div>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                <div className="flex items-end gap-1">
                  <span>date created</span>
                </div>
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap"></th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td colSpan={8}>
                {isLoading ? (
                  <Loader height={65} />
                ) : !isLoading && cases?.length === 0 ? (
                  <EmptyState
                    body={`No ${activeTab} case(s) found`}
                    noBtn
                    height={65}
                  />
                ) : null}
              </td>
            </tr>
          </tbody>

          <tbody className="text-sm bg-white">
            {cases?.length !== 0 &&
              cases?.map((_case, i) => (
                <tr className="border-[0.5px] border-[#E4E3EC]" key={i}>
                  <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-body whitespace-nowrap capitalize">
                    {`${_case?.id?.toUpperCase()}` || '-'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    {shortenLargeNumbers(_case?.transaction?.amount, true, {
                      startAt: 'B',
                    }) || '-'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    <div
                      className={`uppercase py-1 px-2 text-xs font-medium ${
                        getStatusTextAndStyles(_case?.status?.toLowerCase())
                          ?.solid
                      } rounded-full flex items-center w-fit`}
                    >
                      <span className="-mb-[2px]">{_case.status || '-'}</span>
                    </div>
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    {moment(_case?.createdAt).format('Do MMM YYYY h:m a')}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    <button
                      className="h-4 aspect-square"
                      onClick={() =>
                        navigate(`/flows/cases/${slug}/details/${_case.id}`, {
                          state: {
                            status: _case.status,
                            createdAt: moment(_case?.createdAt).format(
                              'Do MMM YYYY h:m a',
                            ),
                            flowName, // Pass flowName in state
                          },
                        })
                      }
                    >
                      <img src={eye} alt="" />
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </TableLayout>

        {!isLoading && cases?.length !== 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
            <PageNumber
              currentPage={currentPage}
              limit={limit}
              totalCount={casesData?.count || 0}
            />

            <div className="flex items-center gap-5">
              <PageLimit
                onLimitChange={setLimit}
                total={casesData && casesData?.count}
                length={cases?.length}
                limit={limit}
              />

              <Pagination
                total={casesData && Math.ceil(casesData?.count / limit)}
                current={+currentPage}
                onPageChange={activePage => {
                  localStorage.setItem('page', String(activePage));
                  pageClick(activePage);
                }}
              />
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
}

export default connect(state => state, Actions)(FlowsCases);
